import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/byrain";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6, width = 1600, height = 900) => ({
  src: `${dir}/${file}.webp`,
  src900: `${dir}/${file}-900.webp`,
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string, height = 1238) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 760,
  height,
  span: 2 as const,
});

export const byrain: CaseStudy = {
  slug: "byrain",
  title: "BYRAIN",
  cardTitle: "Разработка интернет-магазина премиального косметического бренда BYRAIN",
  subtitle: "Премиальная anti-age косметика",
  industry: { label: "Красота и косметология", slug: "beauty" },
  eyebrow: "Интернет-магазин · Премиальная косметика",
  h1: "Разработка интернет-магазина косметического бренда BYRAIN",
  lead:
    "Собрали интернет-магазин премиального бренда BYRAIN: каталог по коллекциям и проблемам кожи, карточку товара с оформлением заказа, научный и брендовый контент и раздел для партнёров.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить e-commerce бренда" },

  seo: {
    primaryKeyword: "разработка интернет-магазина премиального косметического бренда",
    secondaryKeywords: [
      "сайт premium skincare бренда",
      "интернет-магазин anti-age косметики",
      "разработка сайта косметического бренда",
      "e-commerce для косметики",
    ],
    semanticEntities: [
      "коллекции по проблеме кожи",
      "ампульные концентраты",
      "научный подход",
      "клуб бренда",
      "глобальное расширение",
      "корзина и оформление заказа",
      "доставка СДЭК",
      "партнёрская программа",
    ],
    excludedKeywords: [
      "anti-age косметика купить",
      "ампулы для лица",
      "морщины средство",
      "BYRAIN купить",
    ],
    title: "Интернет-магазин BYRAIN — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт BYRAIN с коллекциями anti-age ухода, подбором по проблеме, научным контентом, клубом бренда и e-commerce.",
    h1: "Разработка интернет-магазина косметического бренда BYRAIN",
    slug: "byrain",
    canonicalUrl: `${SITE_URL}/cases/byrain`,
    ogTitle: "Интернет-магазин премиального бренда BYRAIN",
    ogDescription:
      "Каталог по коллекциям и проблемам кожи, карточка товара, checkout, научный и брендовый контент.",
    ogImage: `${SITE_URL}${dir}/byrain-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и оформление заказа",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога и коллекций",
    },
    relatedCases: [
      {
        label: "E-commerce платформа для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Мультибрендовый каталог косметики",
      },
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Каталог и карточка товара для косметического бренда",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт премиального косметического бренда?",
    text: "Спроектируем каталог по коллекциям и проблемам кожи, карточку товара и checkout.",
    button: "Обсудить e-commerce бренда",
  },

  client: {
    name: "BYRAIN",
    url: "https://byrain.ru/",
    city: "Москва",
    industry: "Премиальная косметика",
  },
  year: "2024",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога по коллекциям и проблемам кожи",
    "Карточка товара и checkout",
    "Разделы «Научный подход», клуба и партнёров",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "BYRAIN" },
    { label: "Тип проекта", value: "Интернет-магазин премиального косметического бренда" },
    { label: "Платформа", value: "1С-Битрикс" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог, организованный по коллекциям и проблемам кожи",
      "Карточка товара, корзина и оформление заказа с доставкой по Москве и РФ через СДЭК",
      "Раздел ампульных концентратов и нескольких продуктовых коллекций",
      "Раздел «Научный подход» с описанием подхода бренда",
      "Разделы «Глобальное расширение» и клуб BYRAIN",
      "Раздел для партнёров бренда",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "BYRAIN — премиальный бренд anti-age ухода с ампульными концентратами и несколькими продуктовыми коллекциями. Покупатель приходит с конкретной проблемой кожи, а не с названием средства, поэтому каталог должен предлагать вход и по коллекции, и по проблеме.",
        "Вторая часть задачи — подать бренд на уровне премиального e-commerce: научный подход, клуб бренда и глобальное расширение должны читаться как часть единой истории, а не отдельные информационные страницы.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: премиальная подложка без глянца",
    paragraphs: [
      "Интерфейс собран на глубокой нейтральной подложке — сдержанная премиальность без избыточного золота и глянцевых эффектов.",
      "Каталог по коллекциям и проблемный навигатор работают как два равноценных входа в продуктовую линейку.",
    ],
    bullets: [
      "Нейтральная premium-подложка вместо белого фона",
      "Два входа в каталог: по коллекции и по проблеме кожи",
      "Единая визуальная подача карточки, checkout и брендового контента",
    ],
  },

  identity: {
    fonts: [
      { name: "Montserrat", role: "Заголовки", sample: "Коллекции BYRAIN" },
      { name: "Montserrat", role: "Текст и интерфейс", sample: "Подбор по проблеме кожи" },
    ],
    colors: [
      { hex: "#EDE8E1", name: "Фон витрины" },
      { hex: "#7C6A57", name: "Акцент бренда" },
      { hex: "#1C1B19", name: "Текст" },
    ],
  },

  cover: img(
    "byrain-case-cover",
    "Главная страница интернет-магазина премиального бренда BYRAIN",
    6,
  ),

  gallery: [
    img("byrain-collections", "Каталог BYRAIN по коллекциям и проблемам кожи", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: подбор по проблеме, коллекция, карточка товара и оформление заказа.",
      "Состояния корзины и checkout в мобильной версии показаны заполненными.",
    ],
    images: [
      mobile("byrain-mobile-home", "Мобильная главная интернет-магазина BYRAIN"),
      mobile("byrain-mobile-catalog", "Мобильный каталог BYRAIN по коллекциям"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Подбор по задаче кожи",
      paragraphs: [
        "Каталог даёт два способа входа: через коллекции бренда и через проблемный навигатор по типу задачи кожи. Оба сценария ведут к карточке товара и оформлению заказа.",
      ],
      columns: [
        { title: "Коллекции", items: ["Продуктовые линейки", "Ампульные концентраты"] },
        { title: "Проблемный навигатор", items: ["Подбор по проблеме кожи", "Переход к решению"] },
      ],
    },
    {
      id: "checkout",
      title: "Карточка и checkout",
      paragraphs: [
        "Карточка товара, корзина и оформление заказа собраны в единый сценарий покупки с доставкой по Москве и РФ через СДЭК.",
      ],
      bullets: [
        "Карточка товара с составом и описанием",
        "Корзина с заполненным состоянием",
        "Оформление заказа с доставкой по Москве и РФ",
      ],
    },
    {
      id: "brand",
      title: "Научный и брендовый контент",
      paragraphs: [
        "Разделы «Научный подход», клуб BYRAIN и «Глобальное расширение» показывают бренд шире каталога — как научную и клубную историю.",
      ],
      bullets: [
        "Раздел «Научный подход»",
        "Клуб BYRAIN",
        "Раздел «Глобальное расширение»",
      ],
    },
    {
      id: "partners",
      title: "Партнёрам",
      paragraphs: [
        "Отдельный раздел для партнёров бренда дополняет розничный каталог B2B-фрагментом.",
      ],
      bullets: ["Информация для партнёров бренда"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
    ],
  },
};
